/* eslint-disable no-await-in-loop */
import React, { useMemo, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment/moment';
import debounce from 'lodash/debounce';
import ShadowBlock from '../../../atoms/ShadowBlock';
import Form from '../../../atoms/Form';
import {
  ADDRESS_EXTRA_OPTION,
  ALL_ADDRESSES,
  ALL_COMMUNITIES,
  ALL_MANAGEMENTS,
  COMMUNITIES_EXTRA_OPTION,
  MANAGEMENT_EXTRA_OPTION,
  OnHoldColumns,
  OnHoldFormBoxes,
} from './OnHoldReport.constants';
import MButton from '../../../MUI/Button/MButton';
import Field from '../../../atoms/Field';
import CheckboxSelect from '../../../atoms/CheckboxSelect';

import {
  createOnHoldReport,
  getAddresses,
  getCommunities,
  getManagements,
  getOnHoldReport,
  getOnHoldReportAsCsv,
} from '../../../../store/slices/reportsSlice';
import timeoutPromise from '../../../../utils/timeoutPromise';
import { ReactComponent as ExportIcon } from '../../../../assets/icons/document-download-blue.svg';

import Table from '../../../atoms/Table/Table';
import notificationUtils from '../../../../utils/notificationUtils';
import DateOrTimePicker from '../../../atoms/DateOrTimePicker';
import { ReactComponent as Calender } from '../../../../assets/icons/Calender.svg';
import ProgressModal from '../ProductivityByAttorney/ProgressModal';
import { openOrDownloadBinary } from '../../../../utils/binaryHelpers';
import formatDate from '../../../../utils/formatDate';
import useAsyncLoadDropdown from '../../../../hooks/useAsyncLoadDropdown';

const OnHoldReport = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [lastReportId, setLastReportId] = useState(null);
  const [tableData, setTableData] = useState(null);
  const [addressSearch, setAddressSearch] = useState('');
  const [communitySearch, setCommunitySearch] = useState('');
  const [managementSearch, setManagementSearch] = useState('');

  const { addresses, communities, managementCompanies } = useSelector((state) => state.reports);

  const dispatch = useDispatch();

  const form = useForm({
    defaultValues: {},
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const fieldValues = form.watch();

  const addressArguments = useMemo(() => {
    if (!addressSearch) return {};

    return { address: addressSearch };
  }, [addressSearch]);

  const communityArguments = useMemo(() => {
    if (!communitySearch) return {};

    return { communityName: communitySearch };
  }, [communitySearch]);

  const managementArguments = useMemo(() => {
    if (!managementSearch) return {};

    return { managementName: managementSearch };
  }, [managementSearch]);

  const { handleMenuScrollToBottom: addressScrollToBottom } = useAsyncLoadDropdown({
    hasNextPage: addresses?.hasNextPage,
    actionArguments: addressArguments,
    getAction: ({ page, ...args }) =>
      getAddresses({
        currentPage: page,
        pageSize: 20,
        ...args,
      }),
  });

  const { handleMenuScrollToBottom: communityScrollToBottom } = useAsyncLoadDropdown({
    hasNextPage: communities?.hasNextPage,
    actionArguments: communityArguments,
    getAction: ({ page, ...args }) =>
      getCommunities({
        currentPage: page,
        pageSize: 20,
        ...args,
      }),
  });

  const { handleMenuScrollToBottom: managementScrollToBottom } = useAsyncLoadDropdown({
    hasNextPage: managementCompanies?.hasNextPage,
    actionArguments: managementArguments,
    getAction: ({ page, ...args }) =>
      getManagements({
        currentPage: page,
        pageSize: 20,
        ...args,
      }),
  });

  const getOptions = (name) => {
    switch (name) {
      case 'addresses':
        return addresses?.items?.length ? [ADDRESS_EXTRA_OPTION, ...addresses.items] : [];
      case 'communities':
        return communities?.items?.length ? [COMMUNITIES_EXTRA_OPTION, ...communities.items] : [];
      case 'managements':
        return managementCompanies?.items?.length
          ? [MANAGEMENT_EXTRA_OPTION, ...managementCompanies.items]
          : [];
      default:
        return [];
    }
  };

  const handleSubmit = async (values) => {
    let addressIds = [];
    let communityIds = [];
    let managementIds = [];

    const isAllAddressesSelected = values.addresses?.some((o) => o.value === ALL_ADDRESSES);
    const isAllCommunitiesSelected = values.communities?.some((o) => o.value === ALL_COMMUNITIES);
    const isAllManagementsSelected = values.managements?.some((o) => o.value === ALL_MANAGEMENTS);

    if (!isAllAddressesSelected) {
      addressIds = values.addresses?.length
        ? values.addresses?.map((item) => item.value)
        : undefined;
    }

    if (!isAllCommunitiesSelected) {
      communityIds = values.communities?.length
        ? values.communities?.map((item) => item.value)
        : undefined;
    }

    if (!isAllManagementsSelected) {
      managementIds = values.managements?.length
        ? values.managements?.map((item) => item.value)
        : undefined;
    }

    const data = {
      startDate: moment(values.startDate).format('YYYY-MM-DD'),
      endDate: moment(values.endDate).format('YYYY-MM-DD'),
      addressIds,
      communityIds,
      managementIds,
    };

    setIsLoading(true);

    try {
      const { reportId } = await dispatch(createOnHoldReport(data)).unwrap();
      setLastReportId(reportId);
      let done = false;

      while (!done) {
        const {
          items,
          reportStatus: { code, message },
        } = await dispatch(getOnHoldReport(reportId)).unwrap();
        if (code === 'Completed') {
          const { items: reportItems } = items?.[0] || {};
          setTableData(reportItems);
          done = true;
        } else if (code === 'Error') {
          notificationUtils.error(message);
          done = true;
        } else {
          await timeoutPromise(5000);
        }
      }
    } catch (e) {
      notificationUtils.error(e.message || 'Something went wrong');
    }
    setIsLoading(false);
  };

  const onExport = async () => {
    setIsLoading(true);

    try {
      const data = await dispatch(getOnHoldReportAsCsv(lastReportId)).unwrap();

      openOrDownloadBinary({
        contentType: data.contentType,
        content: data.content,
        fileName: data.originalFileName,
      });
    } catch (e) {
      notificationUtils.error(e.message || 'Something went wrong');
    }

    setIsLoading(false);
  };

  const rowsAdapter = useMemo(() => {
    if (!tableData) {
      return [];
    }

    return tableData?.map((item) => ({
      ...item,
      datePlacedOnHold: formatDate(item?.datePlacedOnHold, 'MM/DD/YY'),
      dateRemovedFromOnHold: formatDate(item?.dateRemovedFromOnHold, 'MM/DD/YY'),
    }));
  }, [tableData]);

  const handleInputChange = (dropdownName) =>
    debounce((value) => {
      switch (dropdownName) {
        case 'addresses':
          return setAddressSearch(value);
        case 'communities':
          return setCommunitySearch(value);
        case 'managements':
          return setManagementSearch(value);
        default:
          return undefined;
      }
    }, 500);

  return (
    <Stack pb={20}>
      <ProgressModal isOpen={isLoading} />
      <ShadowBlock>
        <Form form={form} onSubmit={handleSubmit}>
          <Stack
            direction="row"
            alignItems="center"
            columnGap={3}
            rowGap={2}
            flexWrap="wrap"
            py={1}
          >
            {OnHoldFormBoxes.map((item) => {
              if (item.isDatePicker) {
                return (
                  <Field
                    name={item.name}
                    key={item.name}
                    isMandatory={item.isMandatory}
                    render={({ field, onCustomChange, error }) => (
                      <DateOrTimePicker
                        name={item.name}
                        label={item.label}
                        isDate
                        error={error}
                        Svg={Calender}
                        selected={field.value}
                        isMandatory={item.isMandatory}
                        width={item.width}
                        height={item.height}
                        placeholder={item.placeholder}
                        setSelectedTimeOrDate={onCustomChange(field.onChange)}
                      />
                    )}
                  />
                );
              }

              return null;
            })}
          </Stack>
          <Stack
            direction="row"
            alignItems="center"
            columnGap={3}
            rowGap={2}
            flexWrap="wrap"
            py={1}
          >
            <Field
              name="addresses"
              isMandatory={false}
              render={({ field, onCustomChange }) => (
                <CheckboxSelect
                  value={field.value}
                  isAddDisabled={false}
                  isSearchable
                  label="Address"
                  width="226px"
                  placeholder="-"
                  options={getOptions(field.name)}
                  onChange={onCustomChange(field.onChange)}
                  isMandatory={false}
                  onMenuScrollToBottom={addressScrollToBottom}
                  isClearable
                  onInputChange={handleInputChange(field.name)}
                  isDisabled={fieldValues?.communities?.length || fieldValues?.managements?.length}
                />
              )}
            />
            <Field
              name="communities"
              isMandatory={false}
              render={({ field, onCustomChange }) => (
                <CheckboxSelect
                  value={field.value}
                  isAddDisabled={false}
                  isSearchable
                  label="Community"
                  width="226px"
                  placeholder="-"
                  options={getOptions(field.name)}
                  onChange={onCustomChange(field.onChange)}
                  isMandatory={false}
                  onMenuScrollToBottom={communityScrollToBottom}
                  isClearable
                  onInputChange={handleInputChange(field.name)}
                  isDisabled={fieldValues?.addresses?.length || fieldValues?.managements?.length}
                />
              )}
            />
            <Field
              name="managements"
              isMandatory={false}
              render={({ field, onCustomChange }) => (
                <CheckboxSelect
                  value={field.value}
                  isAddDisabled={false}
                  isSearchable
                  label="Management"
                  width="226px"
                  placeholder="-"
                  options={getOptions(field.name)}
                  onChange={onCustomChange(field.onChange)}
                  isMandatory={false}
                  onMenuScrollToBottom={managementScrollToBottom}
                  isClearable
                  onInputChange={handleInputChange(field.name)}
                  isDisabled={fieldValues?.addresses?.length || fieldValues?.communities?.length}
                />
              )}
            />
          </Stack>

          <Stack
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'flex-start',
              mt: '36px',
            }}
          >
            <MButton type="submit" disabled={isLoading || !form.formState.isValid}>
              Run Report
            </MButton>
          </Stack>
        </Form>
      </ShadowBlock>

      {!!tableData && (
        <Stack mt={4}>
          <ShadowBlock>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography variant="h5" fontWeight={600}>
                On Hold Report
              </Typography>
              <MButton
                variant="bordered"
                startIcon={<ExportIcon />}
                onClick={onExport}
                disabled={tableData?.length === 0}
              >
                Export CSV
              </MButton>
            </Box>
            <Stack rowGap={2}>
              <Table columns={OnHoldColumns} rows={rowsAdapter} />
            </Stack>
          </ShadowBlock>
        </Stack>
      )}
    </Stack>
  );
};

export default OnHoldReport;
